import type { AuditFinding } from '../types';

/**
 * How an audit's findings read on its report: grouped into quick wins and projects, each ordered
 * by impact, with a plain area name instead of the finding kind. The same layout for every audit,
 * built from the findings themselves rather than from the model's markdown.
 */

/** Plain area names for every finding kind agent-suite's audit skills write. */
export const FINDING_AREA_LABELS: Record<string, string> = {
  // Business profile
  category_mismatch: 'Categories',
  description_weak: 'Business description',
  nap_inconsistency: 'Name, address & phone',
  service_coverage_gap: 'Services',
  review_program_gap: 'Reviews',
  qa_seed_gap: 'Questions & answers',
  post_cadence_gap: 'Google posts',
  // Search
  search_term_absent: 'Not showing in search',
  search_term_weak_position: 'Low search position',
  search_term_low_ctr: 'Few clicks from search',
  // Website
  onpage_seo_gap: 'Page titles & tags',
  schema_markup_gap: 'Structured data',
  core_web_vitals_issue: 'Page speed',
  ai_readiness_gap: 'AI readiness',
  conversion_ux_issue: 'Trust & booking',
  content_gap: 'Website content',
  ssl_certificate_issue: 'Security certificate',
  // AI answers
  search_term_ai_absent: 'Missing from AI answers',
  search_term_ai_weak_position: 'Low in AI answers',
  competitor_dominates_ai_answer: 'Competitor leads AI answers',
};

export function findingAreaLabel(kind: string): string {
  return FINDING_AREA_LABELS[kind] ?? kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export type ImpactLevel = 'high' | 'medium' | 'low';

/** Impact is 0-10 (5 when the audit gave none): 7 and up is high, 4 to 6 medium, below 4 low. */
export function impactLevel(impact: number | undefined): ImpactLevel {
  const value = impact ?? 5;
  if (value >= 7) return 'high';
  if (value >= 4) return 'medium';
  return 'low';
}

/** The search term a finding is about, when it is one the practice would recognise. Website
 *  findings carry an internal check key in `query`, which is never shown. */
export function findingSearchTerm(finding: AuditFinding): string | undefined {
  return finding.source === 'gsc' || finding.source === 'ai_visibility' ? finding.query : undefined;
}

function score(finding: AuditFinding): number {
  return (finding.impact ?? 5) * (finding.confidence ?? 0.6);
}

export interface FindingGroups {
  quickWins: AuditFinding[];
  projects: AuditFinding[];
  /** Findings from an audit written before horizons existed. */
  unsorted: AuditFinding[];
}

export function groupFindings(findings: readonly AuditFinding[]): FindingGroups {
  const ordered = [...findings].sort((left, right) => score(right) - score(left));
  return {
    quickWins: ordered.filter((finding) => finding.horizon === 'quick_win'),
    projects: ordered.filter((finding) => finding.horizon === 'project'),
    unsorted: ordered.filter((finding) => finding.horizon === undefined),
  };
}
